<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

const assistants = useMapGetter('captainAssistants/getRecords');
const knowledgeBases = useMapGetter('captainKnowledgeBases/getRecords');
const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');

const selectedBaseIds = ref([]);

const assistant = computed(() =>
  assistants.value.find(item => item.id === props.assistantId)
);

// A base carries the name of every other agent reading it: marking one is how two agents come to
// answer from the same knowledge, and unmarking it only unlinks this one.
const bases = computed(() =>
  knowledgeBases.value.map(base => {
    const otherReaders = (base.assistant_ids || [])
      .filter(id => id !== props.assistantId)
      .map(id => assistants.value.find(item => item.id === id)?.name)
      .filter(Boolean);
    return {
      id: base.id,
      name: base.name,
      description: base.description,
      isSelected: selectedBaseIds.value.includes(base.id),
      otherReaders: otherReaders.length,
      // "Nina e Bia": the names say whose answers change when this base is edited.
      otherReaderNames: new Intl.ListFormat(locale.value.replace('_', '-'), {
        type: 'conjunction',
      }).format(otherReaders),
    };
  })
);

const sharedCount = computed(
  () => bases.value.filter(base => base.isSelected && base.otherReaders).length
);

const hasChanges = computed(() => {
  const current = [...(assistant.value?.knowledge_base_ids || [])].sort();
  const next = [...selectedBaseIds.value].sort();
  return JSON.stringify(current) !== JSON.stringify(next);
});

const toggleBase = id => {
  selectedBaseIds.value = selectedBaseIds.value.includes(id)
    ? selectedBaseIds.value.filter(item => item !== id)
    : [...selectedBaseIds.value, id];
};

const saveBases = async () => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: props.assistantId,
      knowledge_base_ids: selectedBaseIds.value,
    });
    useAlert(t('AGENT_BOTS.BRAIN.BASES.SAVED'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.BRAIN.BASES.SAVE_ERROR'));
  }
};

const openKnowledge = () =>
  router.push({
    name: 'knowledge_bases',
    params: { accountId: route.params.accountId },
  });

watch(
  assistant,
  value => {
    selectedBaseIds.value = [...(value?.knowledge_base_ids || [])];
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('captainKnowledgeBases/get');
});
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
  >
    <div class="flex items-start gap-4">
      <div class="flex flex-col flex-grow gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.BRAIN.BASES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('AGENT_BOTS.BRAIN.BASES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="$t('AGENT_BOTS.BRAIN.BASES.MANAGE')"
        variant="outline"
        color="slate"
        size="sm"
        @click="openKnowledge"
      />
    </div>

    <button
      v-for="base in bases"
      :key="base.id"
      type="button"
      class="flex items-center gap-3 p-3 rounded-lg text-left outline outline-1"
      :class="
        base.isSelected
          ? 'bg-n-alpha-1 outline-n-brand'
          : 'bg-n-alpha-1 outline-n-weak'
      "
      @click="toggleBase(base.id)"
    >
      <span
        class="flex items-center justify-center rounded size-4 shrink-0 outline outline-1"
        :class="
          base.isSelected
            ? 'bg-n-brand outline-n-brand text-white'
            : 'outline-n-strong'
        "
      >
        <span v-if="base.isSelected" class="i-lucide-check size-3" />
      </span>
      <span class="flex flex-col flex-grow min-w-0">
        <span class="text-sm font-medium text-n-slate-12">{{ base.name }}</span>
        <span class="text-sm truncate text-n-slate-11">
          {{ base.description }}
        </span>
      </span>
      <span v-if="base.otherReaders" class="text-xs shrink-0 text-n-slate-11">
        {{
          $t(
            base.isSelected
              ? 'AGENT_BOTS.BRAIN.BASES.OTHER_READERS'
              : 'AGENT_BOTS.BRAIN.BASES.READ_BY',
            { names: base.otherReaderNames }
          )
        }}
      </span>
    </button>

    <p v-if="sharedCount" class="text-xs text-n-amber-11">
      {{ $t('AGENT_BOTS.BRAIN.BASES.SHARED_WARNING') }}
    </p>

    <div class="flex justify-end">
      <Button
        :label="$t('AGENT_BOTS.BRAIN.BASES.SAVE')"
        size="sm"
        :disabled="!hasChanges"
        :is-loading="assistantUiFlags.updatingItem"
        @click="saveBases"
      />
    </div>
  </div>
</template>
